import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import cn from 'classnames'
import { EditorContent, useEditor } from '@tiptap/react'
import { TextSelection } from '@tiptap/pm/state'
import Placeholder from '@tiptap/extension-placeholder'
import Table from '@tiptap/extension-table'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import TableRow from '@tiptap/extension-table-row'
import Underline from '@tiptap/extension-underline'
import StarterKit from '@tiptap/starter-kit'
import classes from './index.module.css'
import { TaskTrackerRichTextImage } from './taskTrackerRichTextImage.extension'
import { TaskTrackerRichTextLink } from './taskTrackerRichTextLink.extension'
import { resolveImageWidthForSave } from '../../lib/taskTrackerDescription.utils'
import TaskTrackerRichTextEditorToolbar from './TaskTrackerRichTextEditorToolbar'
import { useTaskTrackerRichTextAttachments } from './useTaskTrackerRichTextAttachments'
import {
  isEmptyHtml,
  TaskTrackerRichTextEditorContext,
  TaskTrackerRichTextEditorHandle,
  TaskTrackerRichTextEditorProps,
  isSameHtml,
} from './TaskTrackerRichTextEditor.shared'

export type { TaskTrackerRichTextEditorHandle }

const TaskTrackerRichTextEditor = forwardRef<
  TaskTrackerRichTextEditorHandle,
  TaskTrackerRichTextEditorProps
>(({
  value,
  onChange,
  placeholder = 'Добавьте описание задачи...',
  disabled = false,
  compact = false,
  allowAttachments = true,
  onFileReject,
}, ref) => {
  const isApplyingExternalContentRef = useRef(false)
  const [, setToolbarRevision] = useState(0)

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [2, 3],
        },
        codeBlock: false,
        code: false,
      }),
      Underline,
      TaskTrackerRichTextImage.configure({
        inline: false,
        allowBase64: false,
      }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      TaskTrackerRichTextLink.configure({
        openOnClick: true,
        autolink: true,
        defaultProtocol: 'https',
        HTMLAttributes: {
          rel: 'noopener noreferrer',
          target: '_blank',
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    content: value || '',
    editable: !disabled,
    onUpdate: ({ editor: currentEditor }) => {
      if (isApplyingExternalContentRef.current) return

      onChange?.(currentEditor.getHTML())
    },
    editorProps: {
      attributes: {
        class: classes.editorContent,
      },
    },
  })

  const {
    fileInputRef,
    attachmentInputRef,
    handlePickImageFile,
    handleImageFileChange,
    handlePickAttachmentFile,
    handleAttachmentFileChange,
    handlePaste,
    getPendingAttachmentFiles,
    clearPendingAttachmentFiles,
  } = useTaskTrackerRichTextAttachments({
    editor,
    disabled,
    allowAttachments,
    onFileReject,
  })

  const applyExternalContent = useCallback(
    (html: string) => {
      if (!editor) return

      isApplyingExternalContentRef.current = true

      if (isEmptyHtml(html)) {
        editor.commands.clearContent(false)
      } else {
        editor.commands.setContent(html, false)
      }

      if (!html.includes('blob:') && !html.includes('data-blob-url')) {
        clearPendingAttachmentFiles()
      }

      isApplyingExternalContentRef.current = false
    },
    [clearPendingAttachmentFiles, editor],
  )

  useEffect(() => {
    if (!editor) return

    const refreshToolbar = () => setToolbarRevision((revision) => revision + 1)

    editor.on('selectionUpdate', refreshToolbar)
    editor.on('transaction', refreshToolbar)

    return () => {
      editor.off('selectionUpdate', refreshToolbar)
      editor.off('transaction', refreshToolbar)
    }
  }, [editor])

  useEffect(() => {
    if (!editor) return

    editor.setEditable(!disabled)

    if (disabled) {
      const { state, view } = editor
      view.dispatch(state.tr.setSelection(TextSelection.create(state.doc, 0)))
      view.dom.blur()
      return
    }

    requestAnimationFrame(() => {
      if (!editor.isDestroyed && editor.isEditable) {
        editor.commands.focus()
      }
    })
  }, [disabled, editor])

  useEffect(() => {
    if (!editor) return

    const nextValue = value || ''
    const currentValue = editor.getHTML()

    if (isSameHtml(currentValue, nextValue)) return

    applyExternalContent(nextValue)
  }, [applyExternalContent, editor, value])

  useImperativeHandle(
    ref,
    () => ({
      setHtml(html: string) {
        applyExternalContent(html)
      },
      getHtml() {
        if (!editor) return ''

        return editor.getHTML()
      },
      insertText(text: string) {
        if (!editor || !text) return

        editor.chain().focus().insertContent(text).run()
      },
      getImageWidths() {
        if (!editor) return []

        const containerWidth = editor.view.dom.clientWidth || 1
        const widths: string[] = []

        editor.state.doc.descendants((node) => {
          if (node.type.name !== 'image') return

          widths.push(
            resolveImageWidthForSave(node.attrs.width, containerWidth),
          )
        })

        return widths
      },
      getEditorContainerWidth() {
        if (!editor) return 0

        return editor.view.dom.clientWidth || 0
      },
      getPendingAttachmentFiles() {
        return getPendingAttachmentFiles()
      },
      clearPendingAttachmentFiles() {
        clearPendingAttachmentFiles()
      },
    }),
    [applyExternalContent, clearPendingAttachmentFiles, editor, getPendingAttachmentFiles],
  )

  const setLink = useCallback(() => {
    if (!editor) return

    const previousUrl = editor.getAttributes('link').href as string | undefined
    const url = window.prompt('Введите ссылку', previousUrl ?? '')

    if (url === null) return

    if (!url.trim()) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run()
  }, [editor])

  const handleInsertTable = useCallback(() => {
    if (!editor) return

    editor
      .chain()
      .focus()
      .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
      .run()
  }, [editor])

  const handleAddTableRow = useCallback(() => {
    if (!editor) return

    editor.chain().focus().addRowAfter().run()
  }, [editor])

  const handleAddTableColumn = useCallback(() => {
    if (!editor) return

    editor.chain().focus().addColumnAfter().run()
  }, [editor])

  const handleDeleteTableRow = useCallback(() => {
    if (!editor) return

    editor.chain().focus().deleteRow().run()
  }, [editor])

  const handleDeleteTableColumn = useCallback(() => {
    if (!editor) return

    editor.chain().focus().deleteColumn().run()
  }, [editor])

  const handleDeleteTable = useCallback(() => {
    if (!editor) return

    editor.chain().focus().deleteTable().run()
  }, [editor])

  if (!editor) {
    return <div className={classes.editorLoading}>Загрузка редактора...</div>
  }

  return (
    <TaskTrackerRichTextEditorContext.Provider
      value={{ interactionsDisabled: disabled }}
    >
      <div className={cn(classes.root, compact && classes.rootCompact)}>
      {allowAttachments ? (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className={classes.hiddenFileInput}
            onChange={handleImageFileChange}
          />
          <input
            ref={attachmentInputRef}
            type="file"
            className={classes.hiddenFileInput}
            onChange={handleAttachmentFileChange}
          />
        </>
      ) : null}

      {!disabled ? (
        <TaskTrackerRichTextEditorToolbar
          editor={editor}
          onSetLink={setLink}
          onPickImageFile={handlePickImageFile}
          onPickAttachmentFile={handlePickAttachmentFile}
          allowAttachments={allowAttachments}
          onInsertTable={handleInsertTable}
          onAddTableRow={handleAddTableRow}
          onAddTableColumn={handleAddTableColumn}
          onDeleteTableRow={handleDeleteTableRow}
          onDeleteTableColumn={handleDeleteTableColumn}
          onDeleteTable={handleDeleteTable}
        />
      ) : null}

      <div className={classes.editorWrap} onPaste={handlePaste}>
        <EditorContent editor={editor} />
      </div>
    </div>
    </TaskTrackerRichTextEditorContext.Provider>
  )
})

TaskTrackerRichTextEditor.displayName = 'TaskTrackerRichTextEditor'

export default TaskTrackerRichTextEditor
